import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { getInitials } from '@/shared/utils/getInitials';

/** As little of a user as naming one takes, so any directory can supply it. */
interface AvatarStackUser {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

interface AvatarStackProps {
  /** Stored user ids, in the order they were assigned. */
  ids: string[];
  /**
   * The directory the ids are resolved against. Which one it is depends on the
   * board: see the people cell in `CustomCells/ReadOnlyCell`.
   */
  users: AvatarStackUser[];
  /** How many avatars render before the rest collapse into a "+N" chip. */
  max?: number;
  className?: string;
}

const DEFAULT_MAX = 3;

/** A glyph standing in for initials, not copy, so it is the same in every locale. */
const UNKNOWN_INITIAL = '?';

/**
 * Assignees read as blue, and the primitives are named rather than taken from
 * `--accent`: the fallback's own default is the accent pair, which resolves to
 * the taupe of the theme this app runs, not the blue the board was drawn in.
 */
const ASSIGNEE_TINT = 'bg-blue-50 text-blue-600';

/**
 * Overlapping avatars for a set of users.
 *
 * An id the directory does not know (an account that no longer exists, or a
 * directory that has not loaded yet) still gets an avatar, so the stack keeps
 * its size instead of reflowing once the names arrive. It reads as unknown
 * rather than as a name, and says so on hover instead of leaving a bare glyph
 * for the reader to interpret.
 *
 * Every slot takes the same `sm`: the group derives its overlap from that size,
 * and the "+N" chip has to land on the same circle as the avatars beside it.
 */
export function AvatarStack({ ids, users, max = DEFAULT_MAX, className }: AvatarStackProps) {
  const { t } = useTranslation('trackers');

  const visibleIds = ids.slice(0, max);
  const overflow = ids.length - visibleIds.length;

  return (
    <AvatarGroup size="mini" className={className}>
      {visibleIds.map((id) => {
        const user = users.find((u) => u.id === id);
        const name = user?.name ?? t('trackersBoard.avatarStack.unknownUser');
        return (
          <Avatar key={id} size="mini" title={name}>
            {user?.avatarUrl ? <AvatarImage src={user.avatarUrl} alt={name} /> : null}
            <AvatarFallback size="mini" className={ASSIGNEE_TINT}>
              {user ? getInitials(user.name) : UNKNOWN_INITIAL}
            </AvatarFallback>
          </Avatar>
        );
      })}
      {overflow > 0 ? (
        <AvatarGroupCount
          size="mini"
          className={ASSIGNEE_TINT}
          title={t('trackersBoard.avatarStack.moreCount', { count: overflow })}
        >
          +{overflow}
        </AvatarGroupCount>
      ) : null}
    </AvatarGroup>
  );
}
